import React, { useState, useEffect } from 'react';
import { Sparkles, Tag, Clock, Percent, Gift, Zap, Star, ChevronRight, ChevronLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { productsAPI } from '../services/api';

const specialOffers = [
  {
    id: 1,
    title: 'Buy 2 Get 1 Free',
    description: 'On all Indomie Instant Noodles',
    discount: '33% OFF',
    code: 'NOODLE3',
    validUntil: 'Jan 15, 2025',
    type: 'bundle',
    bgColor: 'from-orange-500 to-pink-500',
  },
  {
    id: 2,
    title: 'Free Delivery',
    description: 'On orders over $50',
    discount: 'FREE',
    code: 'FREEDEL',
    validUntil: 'Ongoing',
    type: 'delivery',
    bgColor: 'from-blue-500 to-cyan-500',
  },
  {
    id: 3,
    title: '15% Off Spices',
    description: 'All Island Spice products',
    discount: '15% OFF',
    code: 'SPICE15',
    validUntil: 'Jan 10, 2025',
    type: 'category',
    bgColor: 'from-green-500 to-emerald-500',
  },
  {
    id: 4,
    title: 'Weekend Special',
    description: '$5 off orders over $40',
    discount: '$5 OFF',
    code: 'WEEKEND5',
    validUntil: 'Every weekend',
    type: 'discount',
    bgColor: 'from-purple-500 to-indigo-500',
  },
];

const Offers = ({ onBack }) => {
  const [copiedCode, setCopiedCode] = useState(null);
  const [dealProducts, setDealProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        const products = await productsAPI.getAll();
        // Filter products under $5 and take first 8
        const deals = products.filter(p => p.price < 5 && p.available !== false).slice(0, 8);
        setDealProducts(deals);
      } catch (error) {
        console.error('Error fetching deals:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDeals();
  }, []);

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Back Button */}
      {onBack && (
        <div className="max-w-6xl mx-auto px-4 pt-4">
          <Button 
            variant="ghost" 
            onClick={onBack}
            className="flex items-center gap-1 text-gray-600 hover:text-gray-900"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Shop
          </Button>
        </div>
      )}
      
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, #f97316 0%, #fb923c 50%, #f472b6 100%)'
          }}
        />
        <div className="relative max-w-6xl mx-auto px-4 py-16 text-center text-white">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="w-10 h-10" />
            <h1 className="text-4xl md:text-5xl font-bold">Special Offers & Deals</h1>
            <Sparkles className="w-10 h-10" />
          </div>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Discover amazing savings on your favorite African & Caribbean groceries. 
            New deals added every week!
          </p>
        </div>
      </div>

      {/* Active Promotions */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          <Tag className="w-6 h-6 text-orange-500" />
          Active Promotions
        </h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          {specialOffers.map((offer) => (
            <Card 
              key={offer.id}
              className={`relative overflow-hidden bg-gradient-to-r ${offer.bgColor} text-white`}
            >
              <div className="p-6">
                <Badge className="bg-white/20 text-white mb-3">
                  {offer.type === 'bundle' && <Gift className="w-3 h-3 mr-1" />}
                  {offer.type === 'delivery' && <Zap className="w-3 h-3 mr-1" />}
                  {offer.type === 'category' && <Percent className="w-3 h-3 mr-1" />}
                  {offer.type === 'discount' && <Star className="w-3 h-3 mr-1" />}
                  {offer.discount}
                </Badge>
                
                <h3 className="text-2xl font-bold mb-2">{offer.title}</h3>
                <p className="text-white/90 mb-4">{offer.description}</p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-white/80 text-sm">
                    <Clock className="w-4 h-4" />
                    Valid until: {offer.validUntil}
                  </div>
                  
                  <Button
                    onClick={() => copyCode(offer.code)}
                    className="bg-white/20 hover:bg-white/30 text-white border border-white/30"
                  >
                    {copiedCode === offer.code ? 'Copied!' : `Code: ${offer.code}`}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Deals Under $5 */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Percent className="w-6 h-6 text-green-500" />
            Deals Under $5
          </h2>
          <Button variant="outline" className="flex items-center gap-2">
            View All
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {loading ? (
            <div className="col-span-full flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : dealProducts.length === 0 ? (
            <div className="col-span-full text-center py-8 text-gray-500">
              No deals available at the moment
            </div>
          ) : (
            dealProducts.map((product) => (
              <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-square bg-gray-100">
                  {product.image ? (
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><rect fill="%23f3f4f6" width="200" height="200"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-family="sans-serif" font-size="14">No Image</text></svg>';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <span className="text-gray-400 text-sm">No Image</span>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-gray-800 text-sm line-clamp-2 mb-2">
                    {product.name}
                  </h3>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-green-600">
                      ${product.price.toFixed(2)}
                    </span>
                    <Button size="sm" className="bg-green-500 hover:bg-green-600 text-white">
                      Add
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Newsletter Signup */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <Card className="p-8 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-100">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-3">
              Never Miss a Deal!
            </h2>
            <p className="text-gray-600 mb-6">
              Subscribe to our newsletter and get exclusive offers delivered to your inbox.
            </p>
            <div className="flex gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <Button className="bg-blue-500 hover:bg-blue-600 text-white px-6">
                Subscribe
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Terms */}
      <div className="max-w-6xl mx-auto px-4 pb-12">
        <p className="text-center text-sm text-gray-500">
          * Offers subject to availability. Cannot be combined with other promotions unless specified. 
          See store for details. Prices and availability may vary.
        </p>
      </div>
    </div>
  );
};

export default Offers;
